import React from 'react';
import { money as moneyOf, money0 } from '../../utils/money';
import {
  pct,
  revenueOf,
  apoOf,
  capturedRevenueOf,
  capturedSplitOf,
  enquiriesOf,
  meetingPeriodLabels,
  ytdDeliveredOf,
} from './metrics';
import './PersonMetrics.css';

const money = (v) => moneyOf(v);
const rm0 = (v) => money0(v ?? 0);

// Progress bars pick up the salesperson's identity colour, inherited from
// the `--tone` custom property PersonSection sets on the ancestor
// `.ml-person` section (falls back to the shared default accent when there
// is no such ancestor, e.g. this component rendered standalone in a test).
const TONE = 'var(--tone, var(--rmc-blue))';

function Tile({ name, label, value, progress, footLeft, footRight, note }) {
  return (
    <div className="ml-metric" data-testid={`metric-${name}`}>
      <div className="ml-metric-k">{label}</div>
      <div className="ml-metric-v">{value}</div>
      {progress != null && (
        <div className="ml-metric-prog">
          <i data-testid="metric-bar"
             style={{ width: `${Math.min(progress, 100)}%`, background: TONE }} />
        </div>
      )}
      <div className="ml-metric-foot">
        <span>{footLeft}</span>
        {footRight != null && <b>{footRight}</b>}
      </div>
      {note != null && <div className="ml-metric-note">{note}</div>}
    </div>
  );
}

export default function PersonMetrics({ sec, meetingDate, teamYtdTotal }) {
  const c = sec?.checkins || {};
  // revenueOf/apoOf resolve the booking-tracker shape (captured/capturedApo)
  // as well as the plain shape (actual), so JQ/Zen show real figures too.
  const revenue = revenueOf(sec), floor = c.total?.floor;
  const revPct = pct(revenue, floor);
  // Actual enquiries: the month's count from the lead records (see enquiriesOf),
  // measured against the typed monthly enquiry target.
  const enq = enquiriesOf(sec), enqTarget = c.scorecard?.t?.enq;
  const enqPct = pct(enq, enqTarget);

  // JQ and Zen compare the booking tracker total with a separate captured
  // target. Delivered keeps its own target and Bukku actual above.
  const captured = capturedRevenueOf(sec);
  const bookingDerived = c.total?.source === 'booking';
  const capturedTarget = bookingDerived ? c.total?.capturedTarget : null;
  const capturedPct = bookingDerived ? pct(captured, capturedTarget) : null;
  const split = capturedSplitOf(sec);
  const splitNote = split == null
    ? null
    : `Hunting ${money(split.hunting?.value)} · Lead Gen ${money(split.lead?.value)}`;

  const ytd = ytdDeliveredOf(sec);
  const teamShare = ytd != null && Number(teamYtdTotal) > 0
    ? Number(ytd) / Number(teamYtdTotal) * 100
    : null;
  const periods = meetingPeriodLabels(meetingDate);

  return (
    <div className="ml-person-periods">
      <div className="ml-month-period">
        <div className="ml-period-head">
          <span>This month</span>
          <small>{periods.month}</small>
        </div>
        <div className="ml-metrics">
          <Tile name="revenue" label="Delivered" value={money(revenue)}
                progress={revPct}
                footLeft={floor == null ? 'No target set' : `Target ${rm0(floor)}`}
                footRight={revPct == null ? null : `${Math.round(revPct)}%`} />
          <Tile name="captured" label="Captured" value={money(captured)}
                progress={capturedPct}
                footLeft={bookingDerived
                  ? (capturedTarget == null ? 'No target set' : `Target ${rm0(capturedTarget)}`)
                  : 'Booked this month'}
                footRight={capturedPct == null ? null : `${Math.round(capturedPct)}%`}
                note={splitNote} />
          {/* No progress bar: there is no stored APO target. See the design spec. */}
          <Tile name="apo" label="APO" value={money(apoOf(sec))}
                progress={null} footLeft="Per order" />
          <Tile name="enquiries" label="Enquiries" value={enq == null ? '—' : String(enq)}
                progress={enqPct}
                footLeft={enqTarget == null ? 'No target set' : `Target ${enqTarget}`}
                footRight={enqPct == null ? null : `${Math.round(enqPct)}%`} />
        </div>
      </div>
      <div className="ml-ytd-period" data-testid="metric-ytd">
        <span>Year to date</span>
        <strong>{money0(ytd)}</strong>
        <small>{periods.ytd}</small>
        {teamShare != null && <em>{teamShare.toFixed(1)}% of team</em>}
      </div>
    </div>
  );
}
